import { useEffect, useState } from 'react';
import { getAvailableUnits, getUnitsWithConflicts } from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { AlertTriangle, Check, ChevronsUpDown, X } from 'lucide-react';

// Manual override: add ANY unit (even if booked for the lead's dates) via a
// searchable dropdown. When the parent passes the lead's desired date range we
// surface the conflicting tenant's name + occupancy window so the owner sees
// exactly *why* a unit is blocked. This metadata is admin-only — the endpoint
// (`/available-units?include_unavailable=true`) is never consumed by public
// pages.
export function UnitOverridePicker({
  selectedIds = [], availableIds = [], onToggle,
  startDate = '', endDate = '',
}) {
  const [allUnits, setAllUnits] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loader = (startDate && endDate)
      ? getUnitsWithConflicts(startDate, endDate)
      : getAvailableUnits();
    loader
      .then((units) => { if (!cancelled) setAllUnits(units || []); })
      .catch(() => { if (!cancelled) setAllUnits([]); });
    return () => { cancelled = true; };
  }, [startDate, endDate]);

  const avail = new Set(availableIds);
  const byId = Object.fromEntries(allUnits.map((u) => [u.id, u]));
  const overrides = selectedIds.filter((id) => !avail.has(id) && byId[id]);
  const label = (u) => `${u.property_name} - ${u.unit_number}`;
  const conflictLabel = (u) => {
    if (!u || !u.conflict) return '';
    const { tenant_name, move_in_date, move_out_date } = u.conflict;
    const nm = tenant_name || 'Occupied';
    const range = [move_in_date, move_out_date].filter(Boolean).join(' → ');
    return range ? `${nm} · ${range}` : nm;
  };

  useEffect(() => { if (overrides.length) setEnabled(true); }, [overrides.length]);

  return (
    <div className="space-y-2" data-testid="unit-override-picker">
      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer w-fit">
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="unit-override-switch" />
        Manual override — add any unit
      </label>
      {enabled && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 w-full justify-between text-xs font-normal" data-testid="unit-override-trigger">
              Add any unit (search)… <ChevronsUpDown className="h-3 w-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
            <Command>
              <CommandInput placeholder="Search property or unit…" className="h-9 text-xs" data-testid="unit-override-search" />
              <CommandList className="max-h-60">
                <CommandEmpty>No units found.</CommandEmpty>
                <CommandGroup>
                  {allUnits.map((u) => {
                    const selected = selectedIds.includes(u.id);
                    const unavailable = u.available === false || (!avail.has(u.id) && u.available !== true);
                    const conflict = conflictLabel(u);
                    return (
                      <CommandItem key={u.id} value={`${label(u)} ${u.id}`} onSelect={() => onToggle(u.id)} className="text-xs items-start py-1.5" data-testid={`unit-override-option-${u.id}`}>
                        <Check className={`h-3 w-3 mr-1.5 mt-0.5 ${selected ? 'opacity-100' : 'opacity-0'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span>{label(u)}</span>
                            {unavailable && <span className="text-[10px] text-amber-700">not available</span>}
                          </div>
                          {conflict && (
                            <div className="text-[10px] text-amber-800/80 truncate" data-testid={`unit-override-conflict-${u.id}`}>
                              {conflict}
                            </div>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
      {overrides.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {overrides.map((id) => {
            const u = byId[id];
            const conflict = conflictLabel(u);
            return (
              <Badge key={id} variant="outline" className="text-[11px] gap-1 border-amber-300 bg-amber-50 text-amber-900 items-start py-1 h-auto" data-testid={`unit-override-selected-${id}`}>
                <AlertTriangle className="h-3 w-3 mt-0.5" />
                <span className="flex flex-col items-start leading-tight">
                  <span>{label(u)} · Not available</span>
                  {conflict && (
                    <span className="text-[10px] font-normal opacity-80" data-testid={`unit-override-selected-conflict-${id}`}>
                      {conflict}
                    </span>
                  )}
                </span>
                <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(id); }} className="hover:text-destructive" data-testid={`unit-override-remove-${id}`}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
